import { RpcError } from "grpc-web";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Components
import DataTable from "../../shared/DataTable/DataTable";
import NewPaymentExportButton from "./NewPaymentExportButton";

// Utils
import { DataTableColumn, KVP, PaginatedApiResponse, PaginationResponse, SortDirection } from "../../utils/types";

// Protos
import { PaginationResponse as ApiPaginationResponse } from "../../utils/api/protos/messages/core.message_pb";

// Hooks
import useClaimsToast from "../../hooks/useToast";
import { IParams, getCacheKeyObject } from "../../shared/DataTable/hooks/useSearchParams";

// Utils
import { getClaimExportsList } from "../../utils/api/claims";
import { dummyPaginatedApiResponse } from "../../utils/consts";
import { convertUnixTimestampToLocalDate, handleDocClick } from "../../utils/helpers";
import { Button, Link } from "@chakra-ui/react";
import { DocLink } from "../claims/NewClaim.styles";
import { TableLink } from "../../shared/layout/index.styles";

interface PaymentExportsTableProps {}

const defaultClaimsParams: Partial<IParams> = {
	sortDirection: SortDirection.Desc,
	sortField: "created_at"
};

const PaymentExportsTable: FunctionComponent<PaymentExportsTableProps> = (
	props: PaymentExportsTableProps
): ReactElement => {
	const toast = useClaimsToast();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [exportsData, setExportsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [localCacheKey, setLocalCacheKey] = useState<string>("");
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();

	/**
	 * Define columns to show in the DataTable
	 */
	const columns: Array<DataTableColumn> = [
		{
			header: "Id",
			id: "id",
			filterable: true,
			minWidth: "10ch",
			isHidden: false,
			isSelectable: true,
			isSortable: true
		},
		{
			header: "Name",
			id: "file_name",
			filterable: true,
			minWidth: "20ch",
			isHidden: false,
			isSortable: true,
			allData: true,
			valueMap: (paymentExport: KVP) => (
				<TableLink
					onClick={() => handleDocClick(paymentExport.id as string, toast, false, paymentExport.file_name as string)}>
					{paymentExport.file_name}
				</TableLink>
			)
		},
		{
			header: "Created By",
			id: "created_by_user_name",
			filterable: true,
			minWidth: "20ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "Created At",
			id: "created_at",
			filterable: false,
			isDateTime: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true
		}
	];

	/**
	 * Helper function to allow for child objects to trigger a fetch of data
	 * with the active tab's cacheKey
	 * @param cacheKey The cacheKey of the currently active table
	 */
	const handleFetchData = (cacheKey: string): void => {
		if (!isEmpty(cacheKey)) {
			setIsLoading(true);
			if (cacheKey !== localCacheKey) setLocalCacheKey(cacheKey);
			getClaimExportsList(getCacheKeyObject(cacheKey))
				.then((response) => {
					// Build response data
					const returnedExportsData: Array<{ [key: string]: string | number }> = response
						.getPaymentExportsList()
						.map((paymentExport) => ({
							id: paymentExport.getFileId(),
							file_name: paymentExport.getFileName(),
							created_by_user_name: paymentExport.getCreatedByUserName(),
							created_at: convertUnixTimestampToLocalDate(paymentExport.getCreatedAt()).toLocaleString()
						}));
					const foundPaginationData: ApiPaginationResponse | undefined = response.getPaginationResponse();
					if (foundPaginationData) {
						const returnedPaginationData: PaginationResponse = {
							current_page: foundPaginationData.getCurrentPage(),
							first_page: foundPaginationData.getFirstPage(),
							last_page: foundPaginationData.getLastPage(),
							perPage: foundPaginationData.getLimit(),
							total: foundPaginationData.getTotal()
						};

						setExportsData({
							data: returnedExportsData,
							meta: returnedPaginationData
						});
					} else {
						toast("Error", "There was a problem deciphering the pagination metadata", "error");
						console.error("Attempting to parse pagination metadata that does not exist!!");
					}
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const getCustomSelectedActions = (): ReactElement => <></>;

	const getCustomHeader = (): ReactElement => {
		return (
			<>
				<NewPaymentExportButton onExport={() => handleFetchData(localCacheKey)} />
			</>
		);
	};

	return (
		<>
			<DataTable
				data={exportsData}
				columns={columns}
				refetchTrigger={handleFetchData}
				customHeaderActions={getCustomHeader()}
				customSelectedActions={getCustomSelectedActions}
				defaultParams={defaultClaimsParams}
				existingParams={searchParams}
				shouldUpdateUrl={true}
				isLoading={isLoading}
			/>
		</>
	);
};

export default PaymentExportsTable;
